import { useEffect, useRef, useState } from 'react';
import { Input, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';

import * as ComponentFunctions from './SettlementNameForm.Functions';
import { NAME_MAX_LENGTH, validateName } from './SettlementNameForm.Logic';
import { GuideDrawer, GuideDrawerForm } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideHelpText } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

/** Drawer para cambiar el nombre de una liquidación. Un solo campo, sin vueltas. */
const SettlementNameFormComponent = (props: ComponentFunctions.Props) => {
    const [name, setName] = useState<string>('');
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [touched, setTouched] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Al abrir: arranca con el nombre actual y el foco en el campo.
    useEffect(() => {
        if (!props.isOpen) return;
        setName(props.Settlement?.name ?? '');
        setSubmitted(false);
        setTouched(false);
        const timer = window.setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 50);
        return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.Settlement?.id]);

    // Guardado: se cierra cuando el store confirma el cambio pedido desde acá.
    useEffect(() => {
        if (submitted && !props.isUpdating && props.updatedOk) {
            setSubmitted(false);
            props.onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitted, props.isUpdating, props.updatedOk]);

    useEffect(() => {
        if (props.failOnUpdating) setSubmitted(false);
    }, [props.failOnUpdating]);

    const error = validateName(name, props.Settlement?.name);
    const showError = (touched || submitted) && error;

    const save = () => {
        setSubmitted(true);
        if (error || !props.Settlement) return;
        props.RenameSettlement(props.Settlement.id, name.trim());
    };

    const close = () => { setSubmitted(false); props.onClose(); };

    return (
        <GuideDrawer
            open={props.isOpen}
            size="small"
            title="Cambiar nombre"
            onDismiss={close}
            testId="settlement-name-drawer"
            secondaryAction={{ label: 'Cancelar', disabled: props.isUpdating }}
            primaryAction={{
                label: 'Guardar nombre',
                onClick: save,
                loading: props.isUpdating,
                disabled: !!error && (touched || submitted),
            }}
        >
            <GuideDrawerForm onSubmit={save} testId="settlement-name-form">
                {submitted && props.failOnUpdating && (
                    <GuideMessageBar intent="error" role="alert">
                        <MessageBarBody>
                            <MessageBarTitle>No se pudo guardar el nombre.</MessageBarTitle>
                            {props.error?.ErrorMessage || 'Probá de nuevo.'}
                        </MessageBarBody>
                    </GuideMessageBar>
                )}
                <GuideField
                    label="Nombre de la liquidación"
                    required
                    error={showError ? error : undefined}
                    hint={showError ? undefined : 'Como aparece en la lista, el PDF y las deudas asociadas.'}
                    counter={{ value: name.trim().length, max: NAME_MAX_LENGTH }}
                >
                    <Input
                        ref={inputRef}
                        value={name}
                        maxLength={NAME_MAX_LENGTH + 20}
                        onChange={(_event, data) => { setName(data.value); setTouched(true); }}
                    />
                </GuideField>
            </GuideDrawerForm>
            {props.Settlement?.name && (
                <GuideHelpText>Ahora se llama <strong>{props.Settlement.name}</strong>.</GuideHelpText>
            )}
        </GuideDrawer>
    );
};

export default ComponentFunctions.connector(SettlementNameFormComponent);
